import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { cargasApi, type CargasFilters, type EstadoValidacion, type Carga } from '../api/cargas'
import { vehiculosApi } from '../api/vehiculos'
import { operadoresApi } from '../api/operadores'
import { Table, Pagination, type Column } from '../components/ui/Table'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Select } from '../components/ui/Select'

const ESTADO_OPTIONS = [
  { value: 'VALIDA',             label: 'Válida' },
  { value: 'OUTLIER',            label: 'Outlier' },
  { value: 'PENDIENTE_REVISION', label: 'Pendiente' },
  { value: 'RECHAZADA',          label: 'Rechazada' },
]

const PAGE_SIZE_OPTIONS = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
]

function fmt(n: unknown, dec = 1) {
  if (n === null || n === undefined || n === '') return '—'
  const num = typeof n === 'number' ? n : parseFloat(String(n))
  if (!Number.isFinite(num)) return '—'
  return num.toFixed(dec)
}

export default function Cargas() {
  const navigate = useNavigate()

  const [filters, setFilters] = useState<CargasFilters>({
    page: 1,
    pageSize: 25,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['cargas', filters],
    queryFn: () => cargasApi.list(filters),
  })

  const { data: vehiculosData, isLoading: vehiculosLoading } = useQuery({
    queryKey: ['vehiculos-list'],
    queryFn: () => vehiculosApi.list(),
    staleTime: 5 * 60 * 1000,
  })

  const { data: operadoresData, isLoading: operadoresLoading } = useQuery({
    queryKey: ['operadores-list'],
    queryFn: () => operadoresApi.list(),
    staleTime: 5 * 60 * 1000,
  })

  const vehiculoOptions = (vehiculosData?.items ?? []).map((v) => ({
    value: v.id,
    label: `${v.codigo} (${v.tipoMaquina?.nombre ?? v.tipoMaquinaId.slice(0, 8)})`,
  }))

  const operadorOptions = (operadoresData?.items ?? []).map((o) => ({
    value: o.id,
    label: `${o.nombre} (${o.documentoCi ?? '—'})`,
  }))

  function setFilter<K extends keyof CargasFilters>(key: K, value: CargasFilters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }))
  }

  const exportHref = cargasApi.exportUrl(
    { desde: filters.desde, hasta: filters.hasta, vehiculoId: filters.vehiculoId, operadorId: filters.operadorId, estado: filters.estado },
    'csv',
  )

  const COLUMNS: Column<Carga>[] = [
    {
      key: 'creadoAt',
      header: 'Fecha',
      sortable: true,
      render: (row) => new Date(row.creadoAt).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'short' }),
    },
    {
      key: 'vehiculo',
      header: 'Vehículo',
      render: (row) => row.vehiculo?.codigo ?? row.vehiculoId.slice(0, 8),
    },
    {
      key: 'operador',
      header: 'Operador',
      render: (row) => row.operador?.nombre ?? row.operadorId.slice(0, 8),
    },
    {
      key: 'tanque',
      header: 'Tanque',
      render: (row) => row.tanque?.nombre ?? row.tanqueId.slice(0, 8),
    },
    {
      key: 'litros',
      header: 'Litros',
      sortable: true,
      render: (row) => `${Number(row.litros).toFixed(0)} L`,
      className: 'tabular-nums',
    },
    {
      key: 'montoTotal',
      header: 'Monto BOB',
      sortable: true,
      render: (row) => `Bs. ${Number(row.montoTotal).toFixed(2)}`,
      className: 'tabular-nums',
    },
    {
      key: 'horometroActual',
      header: 'Horómetro',
      render: (row) => `${fmt(row.horometroActual, 0)} h`,
      className: 'tabular-nums',
    },
    {
      key: 'consumoLitrosHora',
      header: 'L/h',
      render: (row) => fmt(row.consumoLitrosHora),
      className: 'tabular-nums',
    },
    {
      key: 'estadoValidacion',
      header: 'Estado',
      render: (row) => <Badge estado={row.estadoValidacion} />,
    },
    {
      key: 'acciones',
      header: '',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/cargas/${row.id}`) }}
          className="text-xs text-primary hover:underline font-medium"
        >
          Ver
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Cargas de combustible</h1>
        <a href={exportHref} download>
          <Button variant="secondary" size="sm">
            Exportar CSV
          </Button>
        </a>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <Input
          type="date"
          label="Desde"
          value={filters.desde ?? ''}
          onChange={(e) => setFilter('desde', e.target.value || undefined)}
        />
        <Input
          type="date"
          label="Hasta"
          value={filters.hasta ?? ''}
          onChange={(e) => setFilter('hasta', e.target.value || undefined)}
        />
        <Select
          label="Vehículo"
          placeholder={vehiculosLoading ? 'Cargando...' : 'Todos'}
          disabled={vehiculosLoading}
          options={vehiculoOptions}
          value={filters.vehiculoId ?? ''}
          onChange={(e) => setFilter('vehiculoId', e.target.value || undefined)}
        />
        <Select
          label="Operador"
          placeholder={operadoresLoading ? 'Cargando...' : 'Todos'}
          disabled={operadoresLoading}
          options={operadorOptions}
          value={filters.operadorId ?? ''}
          onChange={(e) => setFilter('operadorId', e.target.value || undefined)}
        />
        <Select
          label="Estado"
          options={ESTADO_OPTIONS}
          placeholder="Todos"
          value={filters.estado ?? ''}
          onChange={(e) => setFilter('estado', (e.target.value as EstadoValidacion) || undefined)}
        />
      </div>

      {/* Table */}
      <Table
        columns={COLUMNS}
        data={data?.items ?? []}
        keyExtractor={(r) => r.id}
        loading={isLoading}
        emptyMessage="No hay cargas con esos filtros"
        onRowClick={(row) => navigate(`/cargas/${row.id}`)}
      />

      <Pagination
        page={filters.page ?? 1}
        total={data?.total ?? 0}
        pageSize={filters.pageSize ?? 25}
        onPageChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
        onPageSizeChange={(s) => setFilters((prev) => ({ ...prev, pageSize: s, page: 1 }))}
      />

      {/* Page size quick picker */}
      <div className="flex items-center gap-2 text-xs text-slate-400 -mt-2">
        <span>Filas:</span>
        {PAGE_SIZE_OPTIONS.map((o) => (
          <button
            key={o.value}
            onClick={() => setFilters((prev) => ({ ...prev, pageSize: Number(o.value), page: 1 }))}
            className={`px-2 py-0.5 rounded ${filters.pageSize === Number(o.value) ? 'bg-primary text-white' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}
